// The cross-item Trips page (top-nav "Trips"), at /me/trips.
//
// Trips are still owned by an Item -- nothing about the data model changed --
// but the way people use this app is trip-first, so this page reads the whole
// fleet and shows it as three cards, each with an Item column:
//
//   Summary         the fleet's travel logs added up, per year -- the
//                   logbook page's Summary card over every log at once
//   Trips           every item's travel-log entries (Captain's / Pilot's),
//                   newest first
//   Upcoming Trips  every item's trip plans still to come, soonest first
//   Past Trip Plans every item's trip plans that have run their course,
//                   newest first
//
// It used to be one section per item, each with that item's own cards; with
// several boats that was a long page saying the same thing several times. The
// item page keeps its per-item cards (TripsSummary, LogbookSummary), and every
// link from here leads to the existing per-item pages.
//
// A fleet card has no single item to act on, so Add and See All ask which one
// (PickItemModal) -- unless there is exactly one to choose from, in which case
// the question is skipped and the action just runs.
//
// Only items with trip content appear. An item with no travel log and no trip
// at all has nothing to say here and would just be noise; maintenance-only
// items live on the Items page.
//
// Everything under here is wrapped in a RootCrumbProvider, so the pages the
// user lands on read "Trips / Blue Heron / ..." and their first crumb comes
// back here rather than to Items.

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Button from 'react-bootstrap/Button';
import { DataCard, EmptyState, ShowMore, useShowMore, useStatusMessage } from '../../components';
import { mapWithConcurrency, FLEET_READ_CONCURRENCY } from '../../fleet-shared/Concurrency.mjs';
import { CardHeader } from '../../components/CardHeader';
import AddBoatModal from './AddBoatModal';
import PickItemModal from './PickItemModal';
import FleetEntriesTable from './FleetEntriesTable';
import TripGroupsTable from './TripGroupsTable';
import { LogbookSummaryCard } from '../logbook/LogbookSummaryCard';
import { LogbookViewControls } from '../logbook/LogbookViewControls';
import { PhotoErrorAlert } from '../logbook/PhotoErrorAlert';
import { useFleetEntryPhotos } from '../logbook/useEntryPhotos';
import { useLogbookViewPrefs } from '../logbook/useLogbookViewPrefs';
import { useIsMdUp } from '../../utilities/useIsMdUp';
import * as Database from '../../utilities/Database.mjs';
import { loadAllTripsFresh } from '../../utilities/TripSchedule.mjs';
import {
  entryYear,
  isTripLogbookType,
  mergeLogbookSummaries,
  summarizeLogbook,
} from '../../fleet-shared/LogbookTypes.mjs';
import { entriesForYear, tripFieldForType } from '../../fleet-shared/LogbookTrips.mjs';
import {
  groupTrips,
  groupWithStoredLegs,
  isUpcomingTripGroup,
  sortTripGroups,
} from '../../fleet-shared/TripGroups.mjs';
import { tripWithResolvedEndpoints } from '../../fleet-shared/TripRoute.mjs';
import {
  urlForAllItems,
  urlForAllTrips,
  urlForItem,
  urlForLogbookEntry,
  urlForLogbookForId,
  urlForLogbookYear,
  urlForPlaces,
  urlForTrip,
  urlForTripGroup,
  urlForTrips,
} from '../../fleet-shared/Urls.mjs';
import { RootCrumbProvider, useRootCrumbNavigate } from '../item/RootCrumb';
import { usePlaceIndex } from '../../utilities/usePlaceIndex';

const tripsRootCrumb = { name: 'Trips', url: urlForAllTrips() };

// How many recent entries each travel log is read for. It is ALSO the ceiling
// "Show more" on the Trips card can reach: the fleet list is the merge of
// per-log newest-first reads, so it is exact only for its first N rows (any
// entry in the true fleet top-N is in its own log's top-N) and could skip
// entries past that. See All is the way to the rest. Raise both together.
export const RECENT_ENTRIES_PER_LOGBOOK = 20;
// The Trips card's opening length.
const RECENT_ENTRIES_TO_SHOW = 5;
// The Past Trip Plans card's opening length. Upcoming is never capped: across
// a fleet it is still short, and it has no single See All to send the rest to.
const PAST_TRIPS_TO_SHOW = 5;
// The label a travel log gets in the picker. The item's name alone when it is
// the item's only travel log -- "Blue Heron" says it, and "Blue Heron — Blue
// Heron Trips" says it twice -- and qualified by the log's name when the item
// has more than one, since "Captain's Log" on its own could be any boat's.
function logbookChoiceLabel(section, logbook) {
  return section.logbooks.length > 1
    ? `${section.item.name} — ${logbook.name}`
    : section.item.name;
}

// Newest first, compared as INSTANTS rather than as the stored strings
// LogbookSummary sorts on. Within one log the strings sort the way the
// server sorted them, but this list merges logs, and entry dates are not all
// spelled the same way -- databaseDateTimeString writes a local-offset
// timestamp, which does not sort lexicographically against a 'Z' one (the
// listLogbookEntriesOnDay trap) -- so two items' entries from one day could
// interleave wrong. An unparseable date sorts last, stably.
function byDateDesc(a, b) {
  const ta = Date.parse(a.entry.date);
  const tb = Date.parse(b.entry.date);
  const va = Number.isFinite(ta);
  const vb = Number.isFinite(tb);
  if (va && vb) return tb - ta;
  if (va) return -1;
  if (vb) return 1;
  return 0;
}

// The three cards. A child of AllTrips rather than inlined so it renders
// INSIDE the RootCrumbProvider -- useRootCrumbNavigate reads the context, and
// the context has to be above the component calling the hook.
//
// `fleet` is the loaded sections: `{ item, logbooks, legs }` per item, the
// legs STORED rows straight off the read. Grouping happens here, at render,
// because the gazetteer lands on its own schedule and a group's from/to name
// has to follow the index (tripWithResolvedEndpoints); what goes back out
// through a row click is the stored leg, never the display copy
// (groupWithStoredLegs), or a name the gazetteer supplied would reach a write.
//
// `summary` / `summaryLoading` / `entriesByLogbook` are the Summary card's:
// the fleet's travel logs added up (see the second read in AllTrips below),
// null until every whole-log read has landed.
const FleetTrips = ({ fleet, placeIndex, summary, summaryLoading, entriesByLogbook }) => {
  const navigate = useRootCrumbNavigate();
  const [StatusMessage, { showText, showError, hideStatus }] = useStatusMessage();
  // The open question, if any: { title, prompt, choices, onChoose }.
  const [picker, setPicker] = useState(null);

  const storedLegsByItem = useMemo(
    () => new Map(fleet.map((s) => [s.item.id, s.legs])),
    [fleet]
  );

  // Every trip group across the fleet, each tagged with its item -- the tag
  // TripGroupsTable's Item column reads. Group keys are only unique per item,
  // so nothing below may look a group up by key alone.
  const allGroups = useMemo(
    () => fleet.flatMap(({ item, legs }) =>
      groupTrips(
        legs.map((leg) => tripWithResolvedEndpoints(leg, placeIndex)),
        { placeIndex }
      ).map((group) => ({ ...group, item }))
    ),
    [fleet, placeIndex]
  );
  const upcoming = useMemo(
    () => sortTripGroups(allGroups.filter((g) => isUpcomingTripGroup(g)), 'asc'),
    [allGroups]
  );
  const past = useMemo(
    () => sortTripGroups(allGroups.filter((g) => !isUpcomingTripGroup(g)), 'desc'),
    [allGroups]
  );

  const entries = useMemo(
    () => fleet
      .flatMap(({ item, logbooks }) => logbooks.flatMap((logbook) =>
        (logbook.completedTasks?.items ?? []).map((entry) => ({ entry, item, logbook }))
      ))
      .sort(byDateDesc),
    [fleet]
  );

  const entriesMore = useShowMore(
    Math.min(entries.length, RECENT_ENTRIES_PER_LOGBOOK),
    RECENT_ENTRIES_TO_SHOW
  );
  const pastMore = useShowMore(past.length, PAST_TRIPS_TO_SHOW);

  // Hours, notes and photos on the Trips card are for displays that can fit
  // them, the per-trip logbook page's list at md and up. Below md the card is
  // the four-column list it always was, with no switches -- a switch for a
  // column that isn't drawn would be a dead control -- and no S3 read.
  //
  // The switches share the logbook pages' saved preference deliberately:
  // "show me the notes" is one choice about how entry lists read, not one per
  // page, and a reader who turned photos off on a trip page expects them off
  // here too.
  const isMdUp = useIsMdUp();
  const [viewPrefs, setViewPref] = useLogbookViewPrefs();

  // The items whose entries can ever reach the table: Show more stops at
  // RECENT_ENTRIES_PER_LOGBOOK, so a boat past that ceiling has nothing to
  // show a photo under. The hook keys on the ids as a string, so this memo
  // rebuilding is not a refetch; `fleet` loads once, so it rarely does.
  const photoItemIds = useMemo(
    () => [...new Set(entries.slice(0, RECENT_ENTRIES_PER_LOGBOOK).map((row) => row.item.id))],
    [entries]
  );
  const { photosByEntry, photosError, openPhoto } = useFleetEntryPhotos(photoItemIds, {
    enabled: isMdUp && viewPrefs.showPhotos,
  });

  // Ask which item / log, unless there is only one to choose.
  function pick(request) {
    if (request.choices.length === 1) {
      request.onChoose(request.choices[0]);
      return;
    }
    setPicker(request);
  }

  const itemChoices = fleet.map((s) => ({ key: s.item.id, label: s.item.name, item: s.item }));
  const logbookChoices = useMemo(
    () => fleet.flatMap((s) => s.logbooks.map((logbook) => ({
      key: logbook.id,
      label: logbookChoiceLabel(s, logbook),
      item: s.item,
      logbook,
    }))),
    [fleet]
  );

  // ---- Trips (log entries) ----

  function onEntryDetails({ entry, item, logbook }) {
    // The same handover LogbookEntrySummary makes: item + logbook + entry via
    // state so the entry page (useSharedItemPageState) can skip its fetch.
    navigate(urlForLogbookEntry(item.id, logbook.id, entry.id), {
      state: { item, logbook, completedTask: entry },
    });
  }

  function onAddEntry() {
    pick({
      title: 'Add a trip',
      prompt: 'Which log is this trip for?',
      choices: logbookChoices,
      // Create the stub and edit it on its own page. No local-state insert:
      // this page unmounts on navigate and re-reads the fleet on return.
      onChoose: ({ item, logbook }) => Database.createCompletedTaskWithUI(
        logbook, showText, showError, hideStatus,
        (saved) => navigate(urlForLogbookEntry(item.id, logbook.id, saved.id), {
          state: { item, logbook, completedTask: saved },
        })
      ),
    });
  }

  function onSeeAllEntries() {
    pick({
      title: 'See all trips',
      prompt: 'Which log?',
      choices: logbookChoices,
      onChoose: ({ item, logbook }) => navigate(urlForLogbookForId(item.id, logbook.id)),
    });
  }

  // ---- Summary ----

  // The logs a Summary year row can open: those whose type has a per-year
  // page, which is the logbook page's own `tripKey` gate (Captain's yes,
  // Pilot's no -- a flight stands alone, so there is no year of trips to
  // show). No such log, and the rows are plain numbers.
  const yearChoices = useMemo(
    () => logbookChoices.filter((c) => tripFieldForType(c.logbook.logbookType)),
    [logbookChoices]
  );

  // The years at least one of those logs has an entry in. A fleet summary can
  // hold a year whose entries are all a Pilot's flights; that year has no trip
  // page anywhere, so its row is gated off (plain text) rather than opening
  // some other boat's empty year. The same reading of the date summarizeLogbook
  // used to bucket the row, so the two cannot disagree about which year an
  // entry is in.
  const yearsWithTrips = useMemo(() => {
    const years = new Set();
    for (const { logbook } of yearChoices) {
      for (const entry of entriesByLogbook?.get(logbook.id) ?? []) {
        const y = entryYear(entry);
        if (y !== null) years.add(y);
      }
    }
    return years;
  }, [yearChoices, entriesByLogbook]);

  // A year row asks which log's year to open, the Add / See All idiom -- a
  // fleet card has no single log. The question is narrowed to the logs that
  // have an entry in that year, so a year one boat spent alone opens straight
  // away. Non-empty by construction: the row is only clickable for a year in
  // `yearsWithTrips`. The handover is the logbook page's: item + logbook + that
  // year's entries, so the year page can paint before its own read lands.
  function onSelectYear(year) {
    const entriesOf = (logbook) => entriesForYear(entriesByLogbook?.get(logbook.id) ?? [], year);
    pick({
      title: `Trips in ${year}`,
      prompt: 'Which log?',
      choices: yearChoices.filter((c) => entriesOf(c.logbook).length > 0),
      onChoose: ({ item, logbook }) => navigate(urlForLogbookYear(item.id, logbook.id, year), {
        state: { item, logbook, entries: entriesOf(logbook) },
      }),
    });
  }

  // ---- Upcoming Trips / Past Trip Plans ----

  function onGroupDetails(tagged) {
    // Peel the tag off: the group page seeds its legs from `state.group`.
    const { item, ...group } = tagged;
    navigate(urlForTripGroup(item.id, group.key), {
      state: { item, group: groupWithStoredLegs(group, storedLegsByItem.get(item.id) ?? []) },
    });
  }

  function onAddTrip() {
    pick({
      title: 'Add a trip',
      prompt: 'Which item is this trip for?',
      choices: itemChoices,
      onChoose: ({ item }) => Database.createTripWithUI(
        item.id, showText, showError, hideStatus,
        (saved) => navigate(urlForTrip(item.id, saved.id), { state: { item, trip: saved } })
      ),
    });
  }

  function onSeeAllTrips() {
    pick({
      title: 'See all trips',
      prompt: 'Which item?',
      choices: itemChoices,
      onChoose: ({ item }) => navigate(urlForTrips(item.id)),
    });
  }

  // A fleet whose only content is trip plans has no log to add an entry to
  // or to see all of; CardHeader hides a button whose handler is absent.
  const hasLogbooks = logbookChoices.length > 0;

  return (
    <>
      <StatusMessage />

      {/* The logbook page's rule: the skeleton while the whole-log reads are
          out, the card once they land and there is a year to show, nothing
          after a failed read (the status message has already said so). */}
      {summaryLoading
        ? <LogbookSummaryCard loading />
        : summary && summary.byYear.length > 0 && (
          <LogbookSummaryCard
            summary={summary}
            onSelectYear={yearChoices.length > 0 ? onSelectYear : undefined}
            canSelectYear={(year) => yearsWithTrips.has(year)}
          />
        )}

      <DataCard
        header={
          <CardHeader
            text="Trips"
            addTapped={hasLogbooks ? onAddEntry : undefined}
            seeAllTapped={hasLogbooks ? onSeeAllEntries : undefined}
          />
        }
        body={entries.length === 0 ? (
          <p className="text-center text-body-secondary m-0">
            No trips logged yet. Add one to record where you've been.
          </p>
        ) : (
          <>
            {isMdUp && (
              <LogbookViewControls
                showViewToggle={false}
                showNotes={viewPrefs.showNotes}
                onShowNotesChange={(value) => setViewPref('showNotes', value)}
                showPhotosToggle
                showPhotos={viewPrefs.showPhotos}
                onShowPhotosChange={(value) => setViewPref('showPhotos', value)}
              />
            )}
            {/* Inside the card, never the page's StatusMessage -- see
                PhotoErrorAlert, which owns the wording and the dismissal. */}
            {isMdUp && <PhotoErrorAlert error={photosError} show={viewPrefs.showPhotos} />}
            <FleetEntriesTable
              rows={entries.slice(0, entriesMore.shown)}
              placeIndex={placeIndex}
              onSelect={onEntryDetails}
              wide={isMdUp}
              showNotes={viewPrefs.showNotes}
              photosByEntry={isMdUp && viewPrefs.showPhotos ? photosByEntry : null}
              onOpenPhoto={openPhoto}
            />
            {entriesMore.canShowMore && <ShowMore onClick={entriesMore.showMore} />}
          </>
        )}
      />

      <DataCard
        header={<CardHeader text="Upcoming Trips" addTapped={onAddTrip} seeAllTapped={onSeeAllTrips} />}
        body={upcoming.length === 0 ? (
          <p className="text-center text-body-secondary m-0">
            No upcoming trips. Add one to plan your next outing.
          </p>
        ) : (
          <TripGroupsTable groups={upcoming} placeIndex={placeIndex} onDetails={onGroupDetails} showItem />
        )}
      />

      <DataCard
        header={<CardHeader text="Past Trip Plans" seeAllTapped={onSeeAllTrips} />}
        body={past.length === 0 ? (
          <p className="text-center text-body-secondary m-0">
            No past trip plans yet.
          </p>
        ) : (
          <>
            <TripGroupsTable
              groups={past.slice(0, pastMore.shown)}
              placeIndex={placeIndex}
              onDetails={onGroupDetails}
              showItem
            />
            {pastMore.canShowMore && <ShowMore onClick={pastMore.showMore} />}
          </>
        )}
      />

      <PickItemModal
        show={picker != null}
        title={picker?.title}
        prompt={picker?.prompt}
        choices={picker?.choices ?? []}
        onHide={() => setPicker(null)}
        onChoose={(choice) => {
          const { onChoose } = picker;
          setPicker(null);
          onChoose(choice);
        }}
      />
    </>
  );
};

const AllTrips = () => {
  const [StatusMessage, { showLoading, showError, hideStatus }] = useStatusMessage();
  const [fleet, setFleet] = useState(null);
  const [items, setItems] = useState(null);
  const [showAddBoat, setShowAddBoat] = useState(false);
  // The Summary card's read: EVERY entry of every travel log, as a Map from
  // logbook id, null until all of them have landed. A second, independent read
  // of the table the Trips card reads 20 deep -- the logbook page's own split
  // between its summary and its paginated list. Neither is derived from the
  // other on purpose: the cards must not wait for a scan of every log, and a
  // failed scan must not take the Trips card with it. The top 20 rows of each
  // log are read twice; that overlap is the price of the isolation.
  const [entriesByLogbook, setEntriesByLogbook] = useState(null);
  // Without this the skeleton would shimmer for the life of the page after a
  // failed read: the Map stays null and the error only reaches the status line.
  const [summaryFailed, setSummaryFailed] = useState(false);
  const navigate = useNavigate();
  // The gazetteer, handed down to every card so their one-line names match
  // the trip and logbook pages behind them.
  const placeIndex = usePlaceIndex();

  useEffect(() => {
    let mounted = true;
    // Lets the lane waits inside loadAllTripsFresh be dropped on unmount
    // instead of holding queue subscriptions for the life of the page.
    const leaving = new AbortController();

    async function load() {
      try {
        showLoading();
        // Every item, not just top-level ones: a tender or a second aircraft
        // kept as sub-equipment can have its own log, and the has-content
        // filter below already keeps the page short.
        const items = await Database.listAllItems();
        if (!mounted) return;
        // Kept separately from `fleet` (which is filtered to items with trip
        // content) so the empty state below can tell "no items at all" -- a
        // brand-new user -- from "items exist but none has trips yet".
        setItems(items);

        // A few items at a time, not the whole fleet at once: each item is two
        // reads, and a bare Promise.all over a large fleet is a burst of 2N
        // simultaneous AppSync requests. Which items appear is still decided
        // below, after the reads -- there is no cheap "has trip content"
        // probe (hasTripsForItem went with the upcoming-window read), so
        // every item pays for its own answer.
        const loaded = await mapWithConcurrency(items, FLEET_READ_CONCURRENCY, async (item) => {
          const [logbooks, legs] = await Promise.all([
            Database.listLogbooksWithRecentEntries(item.id, RECENT_ENTRIES_PER_LOGBOOK),
            // EVERY leg, not an upcoming window: Past Trip Plans reaches all
            // the way back, and a trip group built from a partial read would
            // draw too short with nothing to say so (the calendar's reason).
            loadAllTripsFresh(item.id, { signal: leaving.signal }),
          ]);
          return {
            item,
            logbooks: logbooks.filter((l) => isTripLogbookType(l.logbookType)),
            legs,
          };
        }, { signal: leaving.signal });
        if (!mounted) return;

        setFleet(
          loaded
            .filter((s) => s.logbooks.length > 0 || s.legs.length > 0)
            .sort((a, b) => a.item.name.localeCompare(b.item.name))
        );
        hideStatus();
      } catch (err) {
        if (mounted) showError(err);
      }
    }

    load();
    return () => { mounted = false; leaving.abort(); };
  }, [hideStatus, showError, showLoading]);

  // The travel logs the fleet holds (already filtered to isTripLogbookType),
  // which is both what the summary reads and what it is summed over.
  const travelLogs = useMemo(
    () => (fleet ?? []).flatMap((s) => s.logbooks),
    [fleet]
  );

  useEffect(() => {
    if (travelLogs.length === 0) return;
    let mounted = true;
    // Stops the pool scheduling the logs still queued once the page goes;
    // a scan already running finishes and is dropped by `mounted`.
    const leaving = new AbortController();
    // Whole-log reads, one per travel log, under the same pool as the fleet
    // read above: a fleet of many logs would otherwise start every scan at
    // once, and each of these is a paged chain over an entire log.
    mapWithConcurrency(travelLogs, FLEET_READ_CONCURRENCY, async (logbook) => (
      [logbook.id, await Database.listCompletedTasksForLogbook(logbook.id)]
    ), { signal: leaving.signal })
      .then((pairs) => { if (mounted) setEntriesByLogbook(new Map(pairs)); })
      .catch((err) => {
        if (!mounted) return;
        setSummaryFailed(true);
        showError(err);
      });
    return () => { mounted = false; leaving.abort(); };
  }, [travelLogs, showError]);

  // One summary per log, each under its OWN type and its own trip keys, then
  // added up -- never summarizeLogbook over the concatenation (see
  // mergeLogbookSummaries for the two ways that would change the numbers).
  const summary = useMemo(
    () => (entriesByLogbook
      ? mergeLogbookSummaries(travelLogs.map((logbook) =>
        summarizeLogbook(entriesByLogbook.get(logbook.id) ?? [], logbook.logbookType)
      ))
      : null),
    [entriesByLogbook, travelLogs]
  );
  const summaryLoading = travelLogs.length > 0 && entriesByLogbook === null && !summaryFailed;

  return (
    <div className="pb-2">
      {/* Grid, not a flex row: the title is centered on the whole page (see
          .all-trips-header) rather than on the space left over beside the
          Places button, which is what a justify-content-between row gives. */}
      <Container className="pt-3 all-trips-header">
        <h3 className="mb-0">Trips</h3>
        <div className="all-trips-actions">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowAddBoat(true)}
          >
            <i className="fa-solid fa-ship me-1" aria-hidden="true"></i>
            Add a boat
          </Button>
          {/* The gazetteer behind every waypoint name. It hangs off this page
              rather than the top nav: it's a tool for the people editing
              routes, not a destination of its own. */}
          <Link to={urlForPlaces()} className="btn btn-sm btn-outline-secondary all-trips-places">
            <i className="fa-solid fa-map-location-dot me-1" aria-hidden="true"></i>
            Places
          </Link>
        </div>
      </Container>

      <StatusMessage />

      {/* Two different empty pages: a brand-new account (no items at all)
          gets the trips-first pitch, while a fleet that just has no trip
          content yet is pointed at both ways in. `items` still null means
          the load hasn't finished -- show neither. */}
      {fleet && fleet.length === 0 && (
        (items ?? []).length === 0 ? (
          <DataCard
            body={
              <EmptyState
                icon="ship"
                title="Track your trips"
                subtitle="Add a boat — or anything you travel on — and log where it's been."
                action={
                  <Button variant="success" onClick={() => setShowAddBoat(true)}>
                    Add a boat
                  </Button>
                }
              />
            }
          />
        ) : (
          <DataCard
            body={
              <p className="text-center text-body-secondary m-0">
                No trips yet. Add a boat here, or add a Captain's Log or an
                upcoming trip from an item's page and it will show up here.{' '}
                <Link to={urlForAllItems()}>Go to Items</Link>
              </p>
            }
          />
        )
      )}

      <AddBoatModal
        show={showAddBoat}
        onHide={() => setShowAddBoat(false)}
        onAdded={(created) => navigate(urlForItem(created.id))}
      />

      <RootCrumbProvider crumb={tripsRootCrumb}>
        {fleet && fleet.length > 0 && (
          <FleetTrips
            fleet={fleet}
            placeIndex={placeIndex}
            summary={summary}
            summaryLoading={summaryLoading}
            entriesByLogbook={entriesByLogbook}
          />
        )}
      </RootCrumbProvider>
    </div>
  );
};

export default AllTrips;
